/**
 * The user's own support ticket — the "Support" tab of the chat panel.
 *
 * No join card: on mount it resolves the user's room (GET /chat/rooms/me)
 * and holds it as SUPPORT_WIDGET_HOLDER; `useHeldRoom` loads and re-syncs
 * the history. There is no "leave" — the ticket is the user's own room.
 *
 * Adds to the shared thread + composer:
 *   - the "Support is now looking at your message" banner (ticket_status);
 *   - an image button: validate client-side (JPEG/PNG/WebP, ≤5 MB), upload,
 *     then send the returned path as an image message.
 */
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useStore } from "react-redux";
import { api } from "@/api/client";
import type { ChatMessage } from "@/api/types";
import type { RootState } from "@/state/store";
import { useChat, useHeldRoom, useRoomViewing } from "@/hooks/useChatSocket";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchMyChatRoom } from "@/state/slices/chatSlice";
import { ChatComposer, useRoomSendState, type ComposerNotice } from "./ChatComposer";
import { ChatThread } from "./ChatThread";
import { errMsg } from "./chatErrors";
import shared from "./ChatRoom.module.css";
import styles from "./SupportWidget.module.css";

/** This component's name as a room holder (see chatSlice `roomHolders`). */
export const SUPPORT_WIDGET_HOLDER = "support-widget";

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const NO_MESSAGES: ChatMessage[] = [];

// ── "Support is now looking at your message" ──────────────────────────────
//
// Shown from the moment ticket_status arrives until the conversation moves
// on — i.e. the next message in the room after the one support picked up:
//   - a supporter's reply: the banner exists so the user isn't "waiting
//     blind until the first reply" (RFC); once the reply is there it has
//     done its job;
//   - the user's own next message: it now refers to an older message.
// "The ticket closed" can't be the trigger: ticket_closed is only broadcast
// to supporters, so the user's client never learns of it. (A close always
// comes after a claim, and a reopen needs a new user message, which already
// hides the banner.)
//
// chatSlice's `claimedBy` gives each claim a `seq` (so a re-claim by the
// same supporter is still a new claim); the widget records, per claim, the
// anchor — the user's latest message when that claim was first seen. It's
// kept per store (not per mount) so the banner stays dismissed after
// switching tabs or closing the panel.
type ClaimAnchor = { seq: number; afterId: number };
const anchorsByStore = new WeakMap<object, Map<string, ClaimAnchor>>();

function anchorsFor(store: object): Map<string, ClaimAnchor> {
  let anchors = anchorsByStore.get(store);
  if (!anchors) {
    anchors = new Map();
    anchorsByStore.set(store, anchors);
  }
  return anchors;
}

function useClaimBanner(roomId: string | null): string | null {
  const store = useStore();
  const claim = useAppSelector((s) => (roomId ? s.chat.claimedBy[roomId] : undefined));
  const messages = useAppSelector((s) =>
    roomId ? s.chat.rooms[roomId]?.messages ?? NO_MESSAGES : NO_MESSAGES,
  );
  const loaded = useAppSelector((s) =>
    roomId ? s.chat.rooms[roomId]?.status === "succeeded" : false,
  );
  const viewerSub = useAppSelector((s) => s.me.sub);
  const [, rerender] = useState(0);

  const anchors = anchorsFor(store);
  const anchor = roomId ? anchors.get(roomId) : undefined;

  // Record the anchor once per claim, after history is in (an empty list
  // mid-load would make every loaded message count as "after").
  useEffect(() => {
    if (!roomId || !claim || !loaded) return;
    if (anchor?.seq === claim.seq) return;
    const lastOwn = [...messages].reverse().find((m) => m.sender_id === viewerSub);
    anchors.set(roomId, { seq: claim.seq, afterId: lastOwn?.id ?? 0 });
    rerender((n) => n + 1);
  }, [roomId, claim, loaded, anchor, messages, viewerSub, anchors]);

  if (!claim || !anchor || anchor.seq !== claim.seq) return null;
  const movedOn = messages.some((m) => m.id > anchor.afterId);
  return movedOn ? null : claim.supporterName;
}

// ── image upload ──────────────────────────────────────────────────────────

type Upload =
  | { phase: "idle" }
  | { phase: "uploading"; name: string }
  // Upload failed: retry re-uploads the same file.
  | { phase: "uploadFailed"; name: string; message: string; file: File }
  // Uploaded, but the socket refused the send: retry resends, no re-upload.
  | { phase: "sendFailed"; name: string; content: string }
  | { phase: "rejected"; message: string };

function imageProblem(file: File): string | null {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return `${file.name}: only JPEG, PNG or WebP images can be sent.`;
  }
  if (file.size > MAX_IMAGE_BYTES) return `${file.name} is over 5 MB.`;
  return null;
}

function ImageIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}

// ── component ─────────────────────────────────────────────────────────────

export function SupportWidget() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { joinRoom, sendMessage } = useChat();
  const roomId = useAppSelector((s) => s.chat.myRoomId);
  const { held } = useHeldRoom(roomId, SUPPORT_WIDGET_HOLDER);
  // Read-marking: only while the thread is actually on screen (held), and
  // on every display — not just when useHeldRoom had to fetch.
  useRoomViewing(roomId && held ? roomId : null);
  const { canSend } = useRoomSendState(roomId);
  const bannerSupporter = useClaimBanner(roomId);

  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolveAttempt, setResolveAttempt] = useState(0);
  // One join per mount/retry: a refused join drops the hold, and re-joining
  // automatically would loop against the refusal.
  const joinTriedRef = useRef(false);
  const [joinRefused, setJoinRefused] = useState(false);
  const [upload, setUpload] = useState<Upload>({ phase: "idle" });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Resolve the room (unless already known), then hold it.
  useEffect(() => {
    if (roomId) return;
    let cancelled = false;
    setResolveError(null);
    dispatch(fetchMyChatRoom())
      .unwrap()
      .catch((e: unknown) => {
        if (!cancelled) setResolveError(`Couldn't open support chat: ${errMsg(e)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, resolveAttempt, dispatch]);

  useEffect(() => {
    if (!roomId || held) return;
    // Read the live holders, not the rendered `held`: under StrictMode this
    // effect re-runs before the re-render that would show our own hold.
    const holders = store.getState().chat.roomHolders[roomId] ?? [];
    if (holders.includes(SUPPORT_WIDGET_HOLDER)) return;
    if (joinTriedRef.current) {
      setJoinRefused(true);
      return;
    }
    joinTriedRef.current = true;
    joinRoom(roomId, SUPPORT_WIDGET_HOLDER);
  }, [roomId, held, joinRoom, store]);

  const retryConnect = () => {
    setJoinRefused(false);
    joinTriedRef.current = false;
    if (roomId) {
      joinTriedRef.current = true;
      joinRoom(roomId, SUPPORT_WIDGET_HOLDER);
    } else {
      setResolveAttempt((n) => n + 1);
    }
  };

  const sendImage = (name: string, content: string) => {
    if (!roomId) return;
    const sent = sendMessage({
      roomId,
      content,
      messageType: "image",
      originalFilename: name,
    });
    setUpload(sent ? { phase: "idle" } : { phase: "sendFailed", name, content });
  };

  const uploadImage = async (file: File) => {
    if (!roomId) return;
    setUpload({ phase: "uploading", name: file.name });
    try {
      const res = await api.chatUploadImage(roomId, file, file.name);
      sendImage(res.original_filename, res.content);
    } catch (e) {
      setUpload({ phase: "uploadFailed", name: file.name, message: errMsg(e), file });
    }
  };

  const onFilePicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    const problem = imageProblem(file);
    if (problem) {
      setUpload({ phase: "rejected", message: problem });
      return;
    }
    void uploadImage(file);
  };

  if (resolveError || joinRefused || !roomId || !held) {
    const failed = resolveError ?? (joinRefused ? "Couldn't connect to support chat." : null);
    return (
      <div className={styles.placeholder}>
        {failed ? (
          <p className={shared.error} role="alert">
            {failed}{" "}
            <button type="button" className={shared.linkButton} onClick={retryConnect}>
              Retry
            </button>
          </p>
        ) : (
          <p className={shared.threadNote}>Connecting to support…</p>
        )}
      </div>
    );
  }

  const dismiss = () => setUpload({ phase: "idle" });
  const uploadNotice: ComposerNotice | null =
    upload.phase === "uploading"
      ? { text: `Uploading ${upload.name}…`, tone: "info" }
      : upload.phase === "uploadFailed"
        ? {
            text: `Couldn't upload ${upload.name}: ${upload.message}`,
            tone: "error",
            action: { label: "Retry", onClick: () => void uploadImage(upload.file) },
            onDismiss: dismiss,
          }
        : upload.phase === "sendFailed"
          ? {
              text: `${upload.name} uploaded but couldn't be sent — chat is disconnected.`,
              tone: "error",
              action: { label: "Retry", onClick: () => sendImage(upload.name, upload.content) },
              onDismiss: dismiss,
            }
          : upload.phase === "rejected"
            ? { text: upload.message, tone: "error", onDismiss: dismiss }
            : null;

  const attach = (
    <>
      <button
        type="button"
        className={shared.iconButton}
        aria-label="Attach image"
        title="Attach image (JPEG, PNG or WebP, up to 5 MB)"
        onClick={() => fileInputRef.current?.click()}
        disabled={!canSend || upload.phase === "uploading"}
      >
        <ImageIcon />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        className={shared.srOnly}
        tabIndex={-1}
        aria-hidden="true"
        data-testid="support-image-input"
        onChange={onFilePicked}
      />
    </>
  );

  return (
    <div className={styles.room}>
      {bannerSupporter ? (
        <div className={styles.banner} role="status">
          <span className={styles.bannerDot} aria-hidden="true" />
          <div>
            Support is now looking at your message · <b>{bannerSupporter}</b>
          </div>
        </div>
      ) : null}
      <ChatThread
        roomId={roomId}
        emptyText="Send a message and the support team will get back to you."
        othersLabel="Support"
      />
      <ChatComposer
        roomId={roomId}
        inputId="support-input"
        placeholder="Message support…"
        leading={attach}
        notices={uploadNotice ? [uploadNotice] : []}
      />
    </div>
  );
}
